import { panUnselectedPlanObject } from "./C1_planObjectPan";
import { roomWallThickness } from "../model/C1_wallThickness";
import { C1_OpeningShading } from "./C1_OpeningShading";
import { itx } from "../../../src/translationItx/itx";
import c1_state from "../model/C1_State";
import { eloItemScaleTransform } from "./C1_eloItemScale";
import { usesFurnishingPlanPresentation } from "./C1_editModePresentation";
import type { C1_Opening, GarageDoorType } from "../types/C1_ModelTypes";
import { useOnChangeWatcher } from "../../../src/models/useOnChangeWatcher";
import { C1_SvgResizeGrab, type C1_ResizeGrabDirection } from "../components/C1_ResizeGrab";

type C1_DoorViewProps = {
  opening: C1_Opening;
  selected: boolean;
  indirectlySelected?: boolean;
  onSelectOpening: (openingId: string) => void;
  onStartDrag: (event: React.PointerEvent<SVGGElement>, openingId: string) => void;
  onStartResize?: (event: React.PointerEvent<SVGGElement>, openingId: string, edge: "start" | "end") => void;
  onDoubleClick?: (openingId: string, clientPoint: { x: number; y: number }) => void;
  interactive?: boolean;
};

type Point = { x: number; y: number };

const SELECTED_OPENING_COLOR = "#f59e0b";
const DOOR_OUTER_FILL = "#efefef";
const FURNISHING_OPENING_LINE_COLOR = "#6b7280";

/** Draws compact plan symbols that distinguish the three garage-door movement principles. */
function GarageDoorSymbol({
  type,
  center,
  along,
  inward,
  length,
  color,
  fill,
}: {
  type: GarageDoorType;
  center: Point;
  along: Point;
  inward: Point;
  length: number;
  color: string;
  fill: string;
}) {
  const point = (alongOffset: number, inwardOffset: number) => ({
    x: center.x + along.x * alongOffset + inward.x * inwardOffset,
    y: center.y + along.y * alongOffset + inward.y * inwardOffset,
  });
  const half = length / 2;
  const start = point(-half, 0);
  const end = point(half, 0);

  if (type === "roller") {
    const slats: JSX.Element[] = [];
    for (let index = 1; index < 8; index += 1) {
      const offset = -half + length * index / 8;
      const slatStart = point(offset, -4);
      const slatEnd = point(offset, 4);
      slats.push(<line key={index} x1={slatStart.x} y1={slatStart.y} x2={slatEnd.x} y2={slatEnd.y} />);
    }
    const edgeA = point(-half, -4);
    const edgeB = point(half, 4);
    return (
      <g fill="none" stroke={color} strokeWidth={1.2}>
        <rect
          x={Math.min(edgeA.x, edgeB.x)}
          y={Math.min(edgeA.y, edgeB.y)}
          width={Math.abs(edgeB.x - edgeA.x) || 8}
          height={Math.abs(edgeB.y - edgeA.y) || 8}
          fill={fill}
        />
        {slats}
      </g>
    );
  }

  const travelDepth = Math.min(length * 0.55, 80);
  const innerStart = point(-half, travelDepth);
  const innerEnd = point(half, travelDepth);
  if (type === "sectional") {
    const sections: JSX.Element[] = [];
    for (let index = 1; index < 4; index += 1) {
      const depth = travelDepth * index / 4;
      const sectionStart = point(-half, depth);
      const sectionEnd = point(half, depth);
      sections.push(
        <line key={index} x1={sectionStart.x} y1={sectionStart.y} x2={sectionEnd.x} y2={sectionEnd.y} opacity={0.55} />,
      );
    }
    return (
      <g fill="none" stroke={color} strokeWidth={1.2}>
        <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} strokeWidth={2.2} />
        <line x1={start.x} y1={start.y} x2={innerStart.x} y2={innerStart.y} strokeDasharray="6 4" />
        <line x1={end.x} y1={end.y} x2={innerEnd.x} y2={innerEnd.y} strokeDasharray="6 4" />
        <line x1={innerStart.x} y1={innerStart.y} x2={innerEnd.x} y2={innerEnd.y} />
        {sections}
      </g>
    );
  }

  const controlStart = point(-half, travelDepth * 0.55);
  const controlEnd = point(half, travelDepth * 0.55);
  return (
    <g fill="none" stroke={color} strokeWidth={1.2}>
      <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} strokeWidth={2.2} />
      <line x1={innerStart.x} y1={innerStart.y} x2={innerEnd.x} y2={innerEnd.y} strokeDasharray="6 4" />
      <path d={`M ${start.x} ${start.y} Q ${controlStart.x} ${controlStart.y} ${innerStart.x} ${innerStart.y}`} />
      <path d={`M ${end.x} ${end.y} Q ${controlEnd.x} ${controlEnd.y} ${innerEnd.x} ${innerEnd.y}`} />
    </g>
  );
}

function doorGeometry(
  side: C1_Opening["side"],
  reflect: boolean,
  centerX: number,
  centerY: number,
  length: number,
  thickness: number,
) {
  const half = length / 2;
  const innerWall = {
    top: { x: centerX, y: centerY + thickness / 2 },
    right: { x: centerX - thickness / 2, y: centerY },
    bottom: { x: centerX, y: centerY - thickness / 2 },
    left: { x: centerX + thickness / 2, y: centerY },
  }[side];
  const along: Point = side === "top" || side === "bottom" ? { x: 1, y: 0 } : { x: 0, y: 1 };
  const inward: Point = {
    top: { x: 0, y: 1 },
    right: { x: -1, y: 0 },
    bottom: { x: 0, y: -1 },
    left: { x: 1, y: 0 },
  }[side];
  const hingeAtPositiveEnd = {
    top: !reflect,
    right: !reflect,
    bottom: reflect,
    left: reflect,
  }[side];
  const hingeSign = hingeAtPositiveEnd ? 1 : -1;
  const hinge = {
    x: innerWall.x + along.x * half * hingeSign,
    y: innerWall.y + along.y * half * hingeSign,
  };
  const closed = {
    x: innerWall.x - along.x * half * hingeSign,
    y: innerWall.y - along.y * half * hingeSign,
  };
  const opened = {
    x: hinge.x + inward.x * length,
    y: hinge.y + inward.y * length,
  };
  const closedVector = { x: closed.x - hinge.x, y: closed.y - hinge.y };
  const openedVector = { x: opened.x - hinge.x, y: opened.y - hinge.y };
  const sweep = closedVector.x * openedVector.y - closedVector.y * openedVector.x > 0 ? 1 : 0;

  return { hinge, closed, opened, sweep };
}

export function C1_DoorView({
  opening,
  selected,
  indirectlySelected = false,
  onSelectOpening,
  onStartDrag,
  onStartResize,
  onDoubleClick,
  interactive = true,
}: C1_DoorViewProps) {
  useOnChangeWatcher(c1_state.settings);
  useOnChangeWatcher(opening.owner);
  const view = c1_state.view;
  const PX = c1_state.svgPx;
  const center = opening.absolutePosition;
  const centerX = (center.x - view.x1) * PX;
  const centerY = (center.y - view.y1) * PX;
  const horizontal = opening.side === "top" || opening.side === "bottom";
  const length = opening.width * PX;
  const room = c1_state.project
    .getFloor(c1_state.currentFloorId.value)
    ?.rooms.find((entry) => entry.id === opening.roomId);
  if (!room) return null;

  const thicknessM = opening.wall ? roomWallThickness(room, opening.wall, c1_state.project)
    : c1_state.project.settings.wallThicknessExterior;
  const thickness = thicknessM * PX;
  const lineColor = selected
    ? SELECTED_OPENING_COLOR
    : indirectlySelected
      ? "#fdba74"
      : usesFurnishingPlanPresentation(c1_state.editMode.value)
        ? FURNISHING_OPENING_LINE_COLOR
        : "#000";
  const outerFill = selected ? "#ffedd5" : indirectlySelected ? "#fff7ed" : DOOR_OUTER_FILL;
  const reflect = opening.reflect ?? false;
  const garageDoor = opening.kind === "garage-door";
  const passage = opening.kind === "door" && opening.doorType === "passage";
  const inward: Point = {
    top: { x: 0, y: 1 },
    right: { x: -1, y: 0 },
    bottom: { x: 0, y: -1 },
    left: { x: 1, y: 0 },
  }[opening.side];
  const along: Point = horizontal ? { x: 1, y: 0 } : { x: 0, y: 1 };
  const slideToPositiveEnd = {
    top: !reflect,
    right: !reflect,
    bottom: reflect,
    left: reflect,
  }[opening.side];
  const originalSideSign = slideToPositiveEnd ? 1 : -1;
  const displayedLeafCount = opening.slidingDoor ? Math.min(opening.doorLeafCount, 2) : opening.doorLeafCount;
  const leafLength = length / displayedLeafCount;
  const leafCenters = Array.from({ length: displayedLeafCount }, (_, index) => {
    const offset = originalSideSign * (length / 2 - leafLength * (index + 0.5));
    return { x: centerX + along.x * offset, y: centerY + along.y * offset };
  });
  const swingLeaves = leafCenters.map((leafCenter, index) =>
    doorGeometry(opening.side, index % 2 === 0 ? reflect : !reflect, leafCenter.x, leafCenter.y, leafLength, thickness),
  );
  const gardenSlidingPair = opening.slidingDoor && opening.doorType === "garden" && displayedLeafCount === 2;
  const slidingLeafThickness = gardenSlidingPair ? Math.min(0.05 * PX, thickness / 3) : 0.05 * PX;
  const trackGap = Math.min(0.01 * PX, thickness / 6);
  const slidingLeaves = leafCenters.map((leafCenter, index) => {
    // The stop-side leaf on the inner track slides in front of the outer leaf,
    // with both tracks and the complete travel inside the wall opening.
    const inwardOffset = gardenSlidingPair
      ? thickness / 2 - slidingLeafThickness / 2 - index * (slidingLeafThickness + trackGap)
      : thickness / 2 + slidingLeafThickness / 2;
    const trackCenter = {
      x: leafCenter.x + inward.x * inwardOffset,
      y: leafCenter.y + inward.y * inwardOffset,
    };
    const leaf = horizontal
      ? {
          x: trackCenter.x - leafLength / 2,
          y: trackCenter.y - slidingLeafThickness / 2,
          width: leafLength,
          height: slidingLeafThickness,
        }
      : {
          x: trackCenter.x - slidingLeafThickness / 2,
          y: trackCenter.y - leafLength / 2,
          width: slidingLeafThickness,
          height: leafLength,
        };
    const slideSign = gardenSlidingPair ? -originalSideSign : index % 2 === 0 ? originalSideSign : -originalSideSign;
    return {
      leaf,
      overhang: gardenSlidingPair && index === 1 ? null : {
        ...leaf,
        x: leaf.x + along.x * slideSign * leafLength,
        y: leaf.y + along.y * slideSign * leafLength,
      },
    };
  });
  const boundsPoints = passage
    ? [
        { x: centerX - (horizontal ? length : thickness) / 2, y: centerY - (horizontal ? thickness : length) / 2 },
        { x: centerX + (horizontal ? length : thickness) / 2, y: centerY + (horizontal ? thickness : length) / 2 },
      ]
    : opening.slidingDoor
    ? slidingLeaves.flatMap(({ leaf, overhang }) => [
        { x: leaf.x, y: leaf.y },
        { x: leaf.x + leaf.width, y: leaf.y + leaf.height },
        ...(overhang ? [
          { x: overhang.x, y: overhang.y },
          { x: overhang.x + overhang.width, y: overhang.y + overhang.height },
        ] : []),
      ])
    : swingLeaves.flatMap(({ hinge, closed, opened }) => [hinge, closed, opened]);
  const doorBounds = {
    x1: Math.min(...boundsPoints.map((point) => point.x)),
    y1: Math.min(...boundsPoints.map((point) => point.y)),
    x2: Math.max(...boundsPoints.map((point) => point.x)),
    y2: Math.max(...boundsPoints.map((point) => point.y)),
  };
  const cutX = horizontal ? centerX - length / 2 : centerX - thickness / 2;
  const cutY = horizontal ? centerY - thickness / 2 : centerY - length / 2;
  const cutWidth = horizontal ? length : thickness;
  const cutHeight = horizontal ? thickness : length;
  const openingCursor =
    selected && c1_state.editMode.value === "building" ? (horizontal ? "ew-resize" : "ns-resize") : "default";
  const label = String(opening.masterIndex ?? "");
  const labelWidth = Math.max(22, label.length * 6 + 10);
  const subtleMasterIndex = c1_state.floorImageRenderOptions.value?.subtleMasterIndices === true;
  const masterIndexBackground = subtleMasterIndex ? "#e5e7eb" : "#173f56";
  const masterIndexColor = subtleMasterIndex ? "#64748b" : "#fff";
  const grabOffset = 16;
  const resizeGrabs = horizontal
    ? [
        { edge: "start" as const, x: cutX - grabOffset, y: centerY },
        { edge: "end" as const, x: cutX + cutWidth + grabOffset, y: centerY },
      ]
    : [
        { edge: "start" as const, x: centerX, y: cutY - grabOffset },
        { edge: "end" as const, x: centerX, y: cutY + cutHeight + grabOffset },
      ];

  return (
    <>
      <g
        focusable="false"
        className={`C1_svgOpening C1_svgDoor${selected ? " C1_selected" : ""}${indirectlySelected ? " C1_indirectlySelected" : ""}`}
        data-c1-opening-id={opening.id}
        data-c1-room-id={opening.roomId}
        style={{
          cursor: interactive ? openingCursor : undefined,
          pointerEvents: interactive ? "all" : "none",
        }}
        onPointerDown={
          interactive
            ? (event) => {
                event.stopPropagation();
                if ((!selected || c1_state.editMode.value !== "building") && panUnselectedPlanObject(event, () => onSelectOpening(opening.id))) return;
                if (selected && c1_state.editMode.value === "building") onStartDrag(event, opening.id);
                else onSelectOpening(opening.id);
              }
            : undefined
        }
        onDoubleClick={
          interactive
            ? (event) => {
                event.stopPropagation();
                onDoubleClick?.(opening.id, { x: event.clientX, y: event.clientY });
              }
            : undefined
        }
      >
        <title>{passage ? itx("c1.ui.Durchgang", "Durchgang") : garageDoor ? "Garagentor" : itx("c1.ui.Tur", "Tür")}</title>
        <rect
          x={cutX}
          y={cutY}
          width={cutWidth}
          height={cutHeight}
          fill={outerFill}
        />

        {horizontal ? (
          <>
            <line x1={cutX} y1={cutY} x2={cutX} y2={cutY + cutHeight} stroke={lineColor} />
            <line x1={cutX + cutWidth} y1={cutY} x2={cutX + cutWidth} y2={cutY + cutHeight} stroke={lineColor} />
          </>
        ) : (
          <>
            <line x1={cutX} y1={cutY} x2={cutX + cutWidth} y2={cutY} stroke={lineColor} />
            <line x1={cutX} y1={cutY + cutHeight} x2={cutX + cutWidth} y2={cutY + cutHeight} stroke={lineColor} />
          </>
        )}

        {passage ? null : garageDoor ? (
          <GarageDoorSymbol
            type={opening.garageDoorType}
            center={{ x: centerX, y: centerY }}
            along={along}
            inward={inward}
            length={length}
            color={lineColor}
            fill={outerFill}
          />
        ) : opening.slidingDoor ? (
          <>
            {slidingLeaves.map(({ leaf, overhang }, index) => (
              <g key={`sliding-leaf-${index}`}>
                <rect {...leaf} fill={lineColor} stroke={lineColor} />
                {overhang && <rect {...overhang} fill={outerFill} stroke={lineColor} strokeWidth={1.2} strokeDasharray="6 4" />}
              </g>
            ))}
          </>
        ) : (
          <>
            {swingLeaves.map(({ hinge, closed, opened, sweep }, index) => (
              <g key={`swing-leaf-${index}`}>
                <path
                  d={`M ${hinge.x} ${hinge.y} L ${closed.x} ${closed.y} A ${leafLength} ${leafLength} 0 0 ${sweep} ${opened.x} ${opened.y} Z`}
                  fill={outerFill}
                  stroke={lineColor}
                  strokeLinejoin="round"
                />
              </g>
            ))}
          </>
        )}

        {opening.kind === "door" && opening.doorType === "garden" && (
          <C1_OpeningShading shading={opening.windowShading} centerX={centerX} centerY={centerY}
            length={length} thickness={thickness} horizontal={horizontal} outwardSide={opening.outwardSide}
            selected={selected} indirectlySelected={indirectlySelected} />
        )}

        {c1_state.settings.showTags &&
          (c1_state.editMode.value === "EloConcept" || c1_state.editMode.value === "EloDetail") &&
          opening.masterIndex !== undefined && (
            <g className="C1_svgMasterIndex" pointerEvents="none" transform={eloItemScaleTransform(centerX, centerY, (c1_state.floorImageRenderOptions.value?.eloItemScale ?? c1_state.settings.eloItemScale))}>
              <rect
                x={centerX - labelWidth / 2}
                y={centerY - 9}
                width={labelWidth}
                height={18}
                rx={9}
                fill={masterIndexBackground}
              />
              <text
                x={centerX}
                y={centerY}
                fill={masterIndexColor}
                fontFamily="Inter, Arial, sans-serif"
                fontSize={10}
                fontWeight={400}
                textAnchor="middle"
                dominantBaseline="central"
                alignmentBaseline="central"
              >
                {label}
              </text>
            </g>
          )}

        <rect
          className="C1_svgOpeningHitArea"
          x={doorBounds.x1 - 8}
          y={doorBounds.y1 - 8}
          width={doorBounds.x2 - doorBounds.x1 + 16}
          height={doorBounds.y2 - doorBounds.y1 + 16}
          fill="transparent"
          stroke="none"
        />
      </g>
      {selected && interactive && c1_state.editMode.value === "building" && (
        <g className="C1_svgDoorResizeGrabs" data-c1-svg-layer="door-resize-grabs" pointerEvents="all"
          onDoubleClick={event => {
            event.preventDefault();
            event.stopPropagation();
            onDoubleClick?.(opening.id, { x: event.clientX, y: event.clientY });
          }}>
          {resizeGrabs.map((grab) => {
            const direction: C1_ResizeGrabDirection = horizontal
              ? grab.edge === "start" ? "left" : "right"
              : grab.edge === "start" ? "top" : "bottom";
            return (
              <C1_SvgResizeGrab
                sizeScale={1 / (c1_state.zoom?.value ?? 1)}
                key={grab.edge}
                data-c1-opening-id={opening.id}
                direction={direction}
                x={grab.x}
                y={grab.y}
                style={{ cursor: horizontal ? "ew-resize" : "ns-resize" }}
                onPointerDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onStartResize?.(event, opening.id, grab.edge);
                }}
              />
            );
          })}
        </g>
      )}
    </>
  );
}
